import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api, { fmtDate, fmtErr, fmtMoney, toastErr } from "../../api";
import { toast } from "sonner";

export default function OwnerDetail() {
  const { id } = useParams();
  const [owners, setOwners] = useState(null);
  const [properties, setProperties] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/owners").then((r) => setOwners(r.data)).catch((e) => toastErr(e));
    api.get("/properties").then((r) => setProperties((Array.isArray(r.data) ? r.data : []).filter((p) => p.owner_id === id))).catch(() => {});
    api.get("/quotes", { params: { owner_id: id } }).then((r) => setQuotes(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load per customer id
  useEffect(() => { load(); }, [id]);

  const owner = owners?.find((o) => o.id === id);
  const invite = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/owners/${id}/reset-password`, {});
      if (r.data.email_sent === false) toast.error("Couldn't send the invite — check the email address");
      else {
        toast.success(`Portal invite emailed to ${owner?.email}`);
        setOwners((os) => os.map((o) => (o.id === id ? { ...o, contact_only: false } : o)));
      }
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  if (owners === null) return <Layout title="Customer"><p className="text-sm text-muted-foreground">Loading…</p></Layout>;
  if (!owner) return <Layout title="Customer"><p className="card-flat p-6 text-sm text-muted-foreground">Customer not found.</p></Layout>;

  return (
    <Layout title={owner.name}>
      <div className="space-y-4" data-testid="customer-page">
        <div className="card-flat space-y-1 p-4" data-testid="customer-card">
          <p className="font-heading text-lg font-bold">{owner.name}</p>
          {owner.email ? <p className="text-sm text-muted-foreground">{owner.email}</p>
            : <p className="text-xs text-muted-foreground">No email on file</p>}
          {owner.phone
            ? <a href={`tel:${owner.phone}`} className="block text-sm font-semibold text-primary">{owner.phone}</a>
            : <p className="text-xs text-muted-foreground">No phone on file</p>}
          {(owner.billing_name || owner.billing_address || owner.billing_email) && (
            <div className="mt-2 rounded-md bg-muted/50 p-2 text-xs" data-testid="customer-billing">
              <p className="font-bold">Billing</p>
              {owner.billing_name && <p>{owner.billing_name}{owner.billing_company ? ` — ${owner.billing_company}` : ""}</p>}
              {owner.billing_email && <p>{owner.billing_email}{owner.billing_cc_email ? ` (cc ${owner.billing_cc_email})` : ""}</p>}
              {owner.billing_same_as_service !== false
                ? <p className="text-muted-foreground">Billing address same as service address</p>
                : owner.billing_address && <p>{owner.billing_address}</p>}
            </div>
          )}
          {owner.contact_only ? (
            <div className="flex items-center gap-2 pt-1">
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-bold text-muted-foreground" data-testid="customer-contact-only">
                Contact only — no portal access
              </span>
              {owner.email && (
                <button data-testid="customer-invite-portal" onClick={invite} disabled={busy}
                  className="text-xs font-bold text-primary underline">Invite to owner portal</button>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{owner.last_login_at ? `Last seen ${fmtDate(owner.last_login_at)}` : "Portal invite sent — hasn't joined yet"}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2" data-testid="customer-actions">
          <Link to={`/quotes/new?owner=${id}&type=one_time`} data-testid="cust-quote-one-time"
            className="flex h-12 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">Quote one-time job</Link>
          <Link to={`/quotes/new?owner=${id}&type=seasonal`} data-testid="cust-quote-seasonal"
            className="flex h-12 items-center justify-center rounded-md border-2 border-primary text-sm font-bold text-primary">Quote seasonal service</Link>
          {properties.length > 0 ? (
            <Link to={`/properties/${properties[0].id}`} data-testid="cust-addon"
              className="flex h-12 items-center justify-center rounded-md border-2 border-border text-sm font-bold">Add-on (log work request)</Link>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="cust-addon-none">Add a property below before logging add-on work.</p>
          )}
        </div>

        <section data-testid="cust-properties">
          <p className="label-caps mb-2">Properties</p>
          <div className="space-y-2">
            {properties.map((p) => (
              <Link key={p.id} to={`/properties/${p.id}`} data-testid={`cust-prop-${p.id}`}>
                <div className="card-flat p-4 transition-colors hover:bg-muted/50">
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.address}</p>
                  {p.site_contact_name && <p className="text-xs text-muted-foreground" data-testid={`cust-prop-site-${p.id}`}>Site contact: {p.site_contact_name}{p.site_contact_phone ? ` · ${p.site_contact_phone}` : ""}</p>}
                </div>
              </Link>
            ))}
            {!properties.length && <p className="text-sm text-muted-foreground">No properties yet.</p>}
          </div>
        </section>

        <section data-testid="cust-quotes">
          <p className="label-caps mb-2">Quotes</p>
          <div className="space-y-2">
            {quotes.map((q) => (
              <Link key={q.id} to={`/quotes/${q.id}`} data-testid={`cust-quote-${q.quote_number}`}>
                <div className="card-flat p-4 transition-colors hover:bg-muted/50">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-bold">{q.quote_number}
                      <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground" data-testid={`cust-quote-type-${q.quote_number}`}>
                        {q.quote_type === "one_time" ? "One-time" : "Seasonal"}
                      </span>
                    </p>
                    <p className="text-sm font-semibold capitalize">{q.status}</p>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {q.quote_type === "one_time"
                      ? `${q.title || "One-time job"} · ${fmtMoney(q.price_totals?.total ?? 0)}`
                      : `${q.season_year} · ${q.visit_count || "—"} ${q.frequency} visits`}
                  </p>
                </div>
              </Link>
            ))}
            {!quotes.length && <p className="text-sm text-muted-foreground">No quotes yet — quote a one-time job or seasonal service above.</p>}
          </div>
        </section>
      </div>
    </Layout>
  );
}
